/**
 * OAuth against Cognito managed login. Authorization code + PKCE, no client secret, because a
 * browser cannot keep one. GadgetCloud never sees a credential: the user either signs in at
 * their own identity provider, at Google, or with a one-time code Cognito emails them.
 */
import { challengeFor, randomString } from './pkce.js';
import type { Session } from './session.js';

export interface AuthClientOptions {
  readonly authDomain: string;
  readonly clientId: string;
  readonly redirectUri: string;
  readonly logoutUri: string;
  readonly fetch?: typeof globalThis.fetch;
  readonly storage?: Storage;
}

export class AuthCallbackError extends Error {}

const VERIFIER = 'gc.pkce.verifier';
const STATE = 'gc.pkce.state';

export function createAuthClient(o: AuthClientOptions) {
  const doFetch = o.fetch ?? globalThis.fetch;
  const storage = o.storage ?? sessionStorage;
  const base = o.authDomain.replace(/\/$/, '');

  /**
   * `idp` picks a path: omitted is passwordless, "Google", or an enterprise provider name.
   * `email` becomes login_hint, so an address typed on our sign-in screen arrives prefilled
   * rather than being asked for twice.
   */
  async function authorizeUrl(idp?: string, email?: string): Promise<string> {
    const verifier = randomString();
    const state = randomString(32);
    storage.setItem(VERIFIER, verifier);
    storage.setItem(STATE, state);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: o.clientId,
      redirect_uri: o.redirectUri,
      scope: 'openid email profile',
      state,
      code_challenge: await challengeFor(verifier),
      code_challenge_method: 'S256',
    });
    if (idp) params.set('identity_provider', idp);
    if (email) params.set('login_hint', email);
    return `${base}/oauth2/authorize?${params.toString()}`;
  }

  async function exchange(body: Record<string, string>): Promise<Session> {
    const res = await doFetch(`${base}/oauth2/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ client_id: o.clientId, ...body }).toString(),
    });
    if (!res.ok) throw new AuthCallbackError(`token endpoint returned ${res.status}`);
    const t = (await res.json()) as { access_token: string; refresh_token?: string; expires_in: number };
    return {
      accessToken: t.access_token,
      refreshToken: t.refresh_token ?? body['refresh_token'] ?? '',
      expiresAt: Date.now() + t.expires_in * 1000,
    };
  }

  /**
   * Completes the redirect. The state check is what stops an attacker feeding us a code they
   * obtained elsewhere, so a mismatch is a hard failure rather than a retry.
   */
  async function handleCallback(search: string): Promise<Session> {
    const params = new URLSearchParams(search);
    const error = params.get('error');
    if (error) throw new AuthCallbackError(params.get('error_description') ?? error);

    const code = params.get('code');
    const state = params.get('state');
    const expectedState = storage.getItem(STATE);
    const verifier = storage.getItem(VERIFIER);
    storage.removeItem(STATE);
    storage.removeItem(VERIFIER);

    if (!code) throw new AuthCallbackError('no authorization code in the callback');
    if (!expectedState || state !== expectedState) throw new AuthCallbackError('state did not match');
    if (!verifier) throw new AuthCallbackError('no PKCE verifier for this browser');

    return exchange({ grant_type: 'authorization_code', code, redirect_uri: o.redirectUri, code_verifier: verifier });
  }

  const refresh = (refreshToken: string): Promise<Session> =>
    exchange({ grant_type: 'refresh_token', refresh_token: refreshToken });

  const logoutUrl = (): string =>
    `${base}/logout?${new URLSearchParams({ client_id: o.clientId, logout_uri: o.logoutUri }).toString()}`;

  return { authorizeUrl, handleCallback, refresh, logoutUrl };
}
export type AuthClient = ReturnType<typeof createAuthClient>;
